import React, { useState } from "react";
import { motion } from "framer-motion";
import { Play, Plus, ImageOff } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { fastIpfsUrl } from "../lib/media";
import MediaModal from "./MediaModal";
import IpfsImage from "./IpfsImage";

// Marketing badges displayed as a corner chip on the product image
const PRODUCT_BADGES = {
    top:       { label: "Top Seller",         emoji: "🔥", bg: "bg-gradient-to-r from-orange-500 to-red-500", border: "border-orange-300" },
    new:       { label: "Nuovo",              emoji: "✨", bg: "bg-gradient-to-r from-[#7c3aed] to-[#e879f9]", border: "border-[#e879f9]" },
    low:       { label: "Poca disponibilità", emoji: "⚠️", bg: "bg-gradient-to-r from-amber-500 to-yellow-500", border: "border-amber-300" },
    available: { label: "Disponibile",        emoji: "✅", bg: "bg-gradient-to-r from-emerald-500 to-green-500", border: "border-emerald-300" },
    promo:     { label: "In Offerta",         emoji: "🏷️", bg: "bg-gradient-to-r from-pink-500 to-rose-500", border: "border-pink-300" },
};

// Parse weight in grams from labels like "100 g", "1 KG", "250g".
const parseWeightGrams = (label = "") => {
    const m = String(label).match(/([\d.,]+)\s*(kg|g)?/i);
    if (!m) return null;
    const n = parseFloat(m[1].replace(",", "."));
    if (!Number.isFinite(n) || n <= 0) return null;
    const unit = (m[2] || "g").toLowerCase();
    return unit === "kg" ? n * 1000 : n;
};

// Pretty label: 1000g → "1 kg" · 5000g → "5 kg" · 250g → "250 g". Falls back to original.
const prettyLabel = (label = "") => {
    const g = parseWeightGrams(label);
    if (g == null) return label;
    if (g >= 1000) {
        const kg = g / 1000;
        return `${Number.isInteger(kg) ? kg : kg.toFixed(1).replace(".", ",")} kg`;
    }
    return `${Number.isInteger(g) ? g : g} g`;
};

const formatEU = (n, dec = 2) =>
    n.toLocaleString("it-IT", { minimumFractionDigits: dec, maximumFractionDigits: dec });

// €10000 → "€10.000" · €700 → "€700" · €2400 → "€2.400"
const formatPrice = (n) =>
    `€${Math.round(n).toLocaleString("it-IT")}`;

const ProductCard = ({ product, index = 0 }) => {
    const { addItem } = useCart();
    const [media, setMedia] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [showDescription, setShowDescription] = useState(false);
    const [selectedVariantIdx, setSelectedVariantIdx] = useState(null);
    const variants = Array.isArray(product.variants) && product.variants.length > 0 ? product.variants : null;
    const photo = product.photo_url ? fastIpfsUrl(product.photo_url) : null;

    const handleAdd = () => {
        if (!product.in_stock) return;
        if (variants) {
            if (selectedVariantIdx === null) return;
            const v = variants[selectedVariantIdx];
            addItem({ ...product, price: v.price, unit: v.label, variant_label: v.label });
        } else {
            addItem(product);
        }
    };

    const canAdd = product.in_stock && (variants ? selectedVariantIdx !== null : true);

    return (
        <>
            <motion.article
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: index * 0.04 }}
                className="group relative bg-[#0a0610] rounded-2xl border border-white/5 ring-1 ring-[#7c3aed]/20 hover:ring-[#e879f9]/60 shadow-[0_4px_20px_rgba(0,0,0,0.6)] hover:shadow-[0_8px_32px_rgba(232,121,249,0.25)] transition-all overflow-hidden flex flex-col"
                data-testid={`product-card-${product.id}`}
            >
                {/* Image area */}
                <div
                    className="relative aspect-[4/3] overflow-hidden bg-[#150826] cursor-pointer"
                    onClick={() => setMedia({ type: "image", url: photo, name: product.name })}
                    data-testid={`view-photo-${product.id}`}
                >
                    {product.photo_url && !loaded && (
                        <div className="absolute inset-0 bg-gradient-to-br from-[#1a0b2e] via-[#0d0418] to-[#1a0b2e] animate-pulse" />
                    )}
                    {product.photo_url ? (
                        <IpfsImage
                            src={photo}
                            alt={product.name}
                            loading={index < 4 ? "eager" : "lazy"}
                            fetchPriority={index < 4 ? "high" : "auto"}
                            decoding="async"
                            onLoad={() => setLoaded(true)}
                            className={`w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700 ${loaded ? "opacity-100" : "opacity-0"}`}
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-white/40">
                            <ImageOff className="w-10 h-10" />
                        </div>
                    )}

                    {/* Video label overlay (top-right, small pill) */}
                    {product.video_url && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setMedia({ type: "video", url: fastIpfsUrl(product.video_url), name: product.name });
                            }}
                            onPointerEnter={() => {
                                const link = document.createElement("link");
                                link.rel = "preload";
                                link.as = "video";
                                link.href = fastIpfsUrl(product.video_url);
                                document.head.appendChild(link);
                            }}
                            className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/70 backdrop-blur-sm border border-[#e879f9]/60 hover:border-[#e879f9] hover:bg-black/85 hover:shadow-[0_0_12px_rgba(232,121,249,0.6)] active:scale-95 transition-all"
                            aria-label="Riproduci video"
                            data-testid={`view-video-${product.id}`}
                        >
                            <Play className="w-2.5 h-2.5 text-[#e879f9]" fill="#e879f9" />
                            <span className="font-mono uppercase tracking-[0.16em] text-[9px] font-bold text-[#e879f9]">
                                Video
                            </span>
                        </button>
                    )}

                    {!product.in_stock && (
                        <div className="absolute top-2 left-2 bg-black/85 backdrop-blur-sm border border-[#e74c5e]/80 text-[#ff8a99] rounded-full px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] font-bold">
                            Esaurito
                        </div>
                    )}

                    {/* Marketing badge (top-left) — hidden when out-of-stock to avoid overlap */}
                    {product.in_stock && product.badge && PRODUCT_BADGES[product.badge] && (
                        <div
                            data-testid={`product-badge-${product.id}`}
                            className={`absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${PRODUCT_BADGES[product.badge].bg} ${PRODUCT_BADGES[product.badge].border} font-mono uppercase tracking-[0.12em] text-[9px] font-bold text-white shadow-[0_2px_10px_rgba(0,0,0,0.5)]`}
                        >
                            <span className="text-[10px] leading-none">{PRODUCT_BADGES[product.badge].emoji}</span>
                            <span>{PRODUCT_BADGES[product.badge].label}</span>
                        </div>
                    )}
                </div>

                {/* Content — generous padding for breathing room */}
                <div className="px-4 pt-4 pb-4 flex flex-col gap-3 flex-1 border-t border-[#7c3aed]/10">
                    {/* Title — Capitalize, not aggressive */}
                    <h3
                        className="font-serif-display text-[17px] sm:text-lg font-semibold leading-[1.15] text-white tracking-tight"
                        data-testid={`product-name-${product.id}`}
                    >
                        {product.name}
                    </h3>

                    {/* Vedi dettaglio link */}
                    {product.description && (
                        <button
                            onClick={() => setShowDescription((v) => !v)}
                            data-testid={`toggle-description-${product.id}`}
                            className="self-start font-mono uppercase tracking-[0.16em] text-[10px] font-bold text-[#e879f9] hover:text-[#f0abfc] transition-colors -mt-1"
                        >
                            {showDescription ? "Nascondi ▴" : "Vedi dettaglio ▾"}
                        </button>
                    )}

                    {showDescription && product.description && (
                        <p
                            className="text-[12px] text-white/65 whitespace-pre-wrap leading-relaxed border-l-2 border-[#7c3aed]/40 pl-2.5"
                            data-testid={`product-description-${product.id}`}
                        >
                            {product.description}
                        </p>
                    )}

                    {/* Variants — 2-col grid, compact stacked layout: taglio €prezzo / €/g */}
                    {variants ? (
                        <div className="grid grid-cols-2 gap-1.5" data-testid={`variants-${product.id}`}>
                            {variants.map((v, i) => {
                                const isSelected = selectedVariantIdx === i;
                                const grams = parseWeightGrams(v.label);
                                const perGram = grams ? v.price / grams : null;
                                return (
                                    <button
                                        key={v.label + i}
                                        onClick={() => setSelectedVariantIdx(i)}
                                        disabled={!product.in_stock}
                                        data-testid={`variant-${product.id}-${i}`}
                                        aria-pressed={isSelected}
                                        className={
                                            "px-2 py-1.5 rounded-xl border active:scale-[0.96] transition-all disabled:opacity-40 disabled:cursor-not-allowed text-center [&:last-child:nth-child(odd)]:col-span-2 " +
                                            (isSelected
                                                ? "border-[#e879f9] bg-gradient-to-br from-[#e879f9]/15 to-[#7c3aed]/15 shadow-[0_0_12px_rgba(232,121,249,0.4)]"
                                                : "border-white/10 bg-white/[0.025] hover:border-[#7c3aed]/70")
                                        }
                                    >
                                        <div className="flex items-baseline justify-center gap-1.5 leading-none">
                                            <span className="font-mono font-bold text-[12px] text-white/85 tabular-nums">
                                                {prettyLabel(v.label)}
                                            </span>
                                            <span className={`font-mono font-bold text-[13px] tabular-nums ${isSelected ? "text-[#f0abfc]" : "text-white"}`}>
                                                {formatPrice(v.price)}
                                            </span>
                                        </div>
                                        {perGram && (
                                            <div className={`font-mono text-[9px] leading-none mt-1 tabular-nums ${isSelected ? "text-[#e879f9]" : "text-white/40"}`}>
                                                €{formatEU(perGram)}/g
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="flex items-baseline gap-1 py-1">
                            <span className="font-mono text-2xl font-bold text-[#e879f9] drop-shadow-[0_0_8px_rgba(232,121,249,0.4)]" data-testid={`product-price-${product.id}`}>
                                {formatPrice(product.price)}
                            </span>
                            {product.unit && <span className="font-mono text-[11px] text-white/50">/{product.unit}</span>}
                        </div>
                    )}

                    {/* Add to cart button */}
                    <button
                        onClick={handleAdd}
                        disabled={!canAdd}
                        data-testid={`add-to-cart-${product.id}`}
                        className="mt-2 w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-full bg-gradient-to-r from-[#7c3aed] to-[#9333ea] hover:from-[#a78bfa] hover:to-[#e879f9] text-white font-mono uppercase tracking-[0.14em] text-[11px] font-bold shadow-[0_4px_14px_rgba(124,58,237,0.5)] hover:shadow-[0_6px_22px_rgba(232,121,249,0.7)] active:scale-[0.97] transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:from-[#7c3aed] disabled:hover:to-[#9333ea] disabled:shadow-none"
                    >
                        <Plus className="w-3.5 h-3.5" strokeWidth={3} />
                        {variants && selectedVariantIdx === null ? "Seleziona taglio" : "Aggiungi"}
                    </button>
                </div>
            </motion.article>

            <MediaModal media={media} onClose={() => setMedia(null)} />
        </>
    );
};

export default ProductCard;
